<template>
  <div :class="classes">
    <label v-if="labelAbove" :for="$attrs.id" class="input__label-above">
      <m4-icon v-if="labelIcon" :icon="labelIcon"></m4-icon>
      {{ labelAbove }}
    </label>
    <textarea
      v-if="isTextarea"
      v-bind="$attrs"
      class="input__field"
      v-model="computedValue"
      @blur="onBlur"
      @keyup="$emit('keyup', $event.target.value)"
    ></textarea>
    <input
      v-else
      v-bind="$attrs"
      :class="inputFieldClasses"
      v-model="computedValue"
      ref="inputElm"
      @keyup="$emit('keyup', $event.target.value)"
      :placeholder="defaultPlaceholder"
      @blur="onBlur"
      @focus="$emit('focus', $event.target.value)"
    />
    <label ref="floatingPlaceholder" v-if="$attrs.placeholder" :for="$attrs.id" :class="floatingPlaceholderClasses">
      {{ $attrs.placeholder }}
    </label>
  </div>
</template>

<script>
import inBrowser from "../../../utils/inBrowser.js";
import Inputmask from "inputmask";
import callExternalFunction from "../../../utils/callExternalFunction";

export default {
  name: "M4Input",
  mixins: [callExternalFunction],
  data() {
    return {
      newValue: this.modelValue ? this.modelValue : this.value,
      allowPlaceholderAnimation: false,
      floatingPlaceholderRows: 1
    };
  },
  props: {
    error: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number]
    },
    modelValue: {
      type: [String, Number]
    },
    mask: {
      type: [Object, String]
    },
    labelAbove: String,
    labelIcon: String,
    defaultPlaceholder: String,
    trimOnBlur: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "blur", "focus", "keyup", "init"],
  computed: {
    classes() {
      let classes = ["input"];
      if (this.$attrs.placeholder) {
        classes.push("input--floating-placeholder");
      }
      if (this.isDisabled) {
        classes.push("input--disabled");
      }
      if (this.labelAbove) {
        classes.push("input--label-above");
      }
      if (this.newValue) {
        classes.push("input--has-value");
      }
      if (this.isTextarea) {
        classes.push("input--textarea");
      }
      if (this.allowPlaceholderAnimation) {
        classes.push("input--allow-placeholder-animation");
      }

      classes.push("input--floating-placeholder-rows-" + this.floatingPlaceholderRows);

      return classes;
    },
    inputFieldClasses() {
      let classes = ["input__field"];
      if (this.error) {
        classes.push("input__field--error");
      }
      return classes;
    },
    floatingPlaceholderClasses() {
      let classes = ["input__floating-placeholder"];
      if (this.error) {
        classes.push("input__floating-placeholder--error");
      }
      return classes;
    },
    isTextarea() {
      return this.$attrs.type === "textarea";
    },
    isDisabled() {
      return this.$attrs.disabled !== undefined;
    },
    computedValue: {
      get() {
        return this.newValue;
      },
      set(value) {
        this.newValue = value;
        this.$emit("update:modelValue", value);
        this.callExtFunc("updateModelValue", value);
      }
    }
  },
  methods: {
    onBlur() {
      if (this.trimOnBlur && this.newValue) {
        this.newValue = this.newValue.replace(/\s+/g, " ").trim();
      }
      this.$emit("blur", this.newValue);
    }
  },
  watch: {
    /**
     * When v-model is changed:
     *   1. Set internal value.
     */
    modelValue(value) {
      this.newValue = value;
    },
    value(value) {
      this.newValue = value;
    }
  },
  mounted() {
    if (this.mask && this.$refs.inputElm) {
      if (inBrowser()) {
        Inputmask(this.mask).mask(this.$refs.inputElm);
      }
    }
    setTimeout(() => {
      this.allowPlaceholderAnimation = true;
    }, 600);

    if (this.$refs.floatingPlaceholder && this.$refs.floatingPlaceholder.offsetHeight >= 14) {
      this.floatingPlaceholderRows = Math.floor(this.$refs.floatingPlaceholder.offsetHeight / 11);
    }

    this.$nextTick(function () {
      this.$emit("init", this.newValue);
      this.callExtFunc("init", this.newValue);
    });
  }
};
</script>
